<script>
let formatter = null;

export default {
    props: {
        lineItems: {},
        itemNumber: {},
        currency: { default: "usd" },
    },
    data: () => ({
        sort: "lineNumber",
        dir: "asc",
        page: 1,
        limit: 25,
    }),
    watch: {
        itemNumber() {
            if (this.page > 1) {
                this.page = 1;
            }
        },
    },
    computed: {
        filteredLineItems() {
            const items = [
                ...this.lineItems.filter((i) =>
                    i.itemNumber.includes(this.itemNumber || "")
                ),
            ];
            items.sort((a, b) => {
                if (a[this.sort] > b[this.sort]) {
                    return this.dir === "asc" ? 1 : -1;
                } else if (a[this.sort] < b[this.sort]) {
                    return this.dir === "asc" ? -1 : 1;
                } else {
                    return 0;
                }
            });

            return items;
        },
        pages() {
            return Math.ceil(this.filteredLineItems.length / this.limit);
        },
        paginatedLineItems() {
            return this.filteredLineItems.slice(
                (this.page - 1) * this.limit,
                this.page * this.limit
            );
        },
        resultsDisplayed() {
            return Math.min(
                this.limit,
                this.filteredLineItems.length - (this.page - 1) * this.limit
            );
        },
    },
    methods: {
        setPage(n) {
            this.page = n;
            this.scrollToTableWithOffset();
        },
        formatCurrency(amt) {
            return formatter.format(amt);
        },
        sortOn(field) {
            if (field != this.sort) {
                this.sort = field;
                this.dir = "asc";
            } else {
                this.dir = this.dir == "asc" ? "desc" : "asc";
            }
        },

        scrollToTableWithOffset() {
            scrollTo(
                0,
                window.pageYOffset +
                    this.$refs.table.getBoundingClientRect().top -
                    300
            );
        },

        linkPackingSlipNumbers(numbers) {
            return numbers
                .map(n => `<a target="_blank" href="${location.pathname}/packing-slip/${n}">${n}</a>`)
                .join(' ')
        },

        linkInvoiceNumbers(invoiceNumbers) {
            return invoiceNumbers
                .filter(({ invoiceNumber }) => invoiceNumber)
                .map(({ hasInvoice, invoiceNumber }) => {
                    if (hasInvoice) {
                        return `<a target="_blank" href="${location.pathname}/invoice/${invoiceNumber}">${invoiceNumber}</a>`;
                    } else {
                        return invoiceNumber;
                    }
                })
                .join(" ");
        },
    },

    created() {
        formatter = new Intl.NumberFormat(navigator.language || "en-us", {
            style: "currency",
            currency: this.currency || "usd",
        });
    },
};
</script>
<template>
    <div>
        <table v-if="lineItems.length" class="lineItems" ref="table">
            <thead>
                <tr>
                    <th class="sortHeader" @click="sortOn('lineNumber')">
                        Line
                        <svg
                            width="11"
                            height="14"
                            viewBox="0 0 11 14"
                            class="sortHeader__icon"
                            :class="
                                sort == 'lineNumber' ? `-active -${dir}` : ''
                            "
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M0.810974 8.34668L5.5607 13.0964L10.3104 8.34668H0.810974Z"
                                fill="currentColor"
                            />
                            <path
                                d="M10.3105 5.09668L5.56076 0.346956L0.811037 5.09668L10.3105 5.09668Z"
                                fill="currentColor"
                            />
                        </svg>
                    </th>
                    <th class="sortHeader" @click="sortOn('itemNumber')">
                        ASC Item #
                        <svg
                            width="11"
                            height="14"
                            viewBox="0 0 11 14"
                            class="sortHeader__icon"
                            :class="
                                sort == 'itemNumber' ? `-active -${dir}` : ''
                            "
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M0.810974 8.34668L5.5607 13.0964L10.3104 8.34668H0.810974Z"
                                fill="currentColor"
                            />
                            <path
                                d="M10.3105 5.09668L5.56076 0.346956L0.811037 5.09668L10.3105 5.09668Z"
                                fill="currentColor"
                            />
                        </svg>
                    </th>
                    <th class="sortHeader">Description</th>
                    <th class="sortHeader">Quantity</th>
                    <th class="sortHeader">Net Price</th>
                    <th class="sortHeader" @click="sortOn('status')">
                        Status
                        <svg
                            width="11"
                            height="14"
                            viewBox="0 0 11 14"
                            class="sortHeader__icon"
                            :class="sort == 'status' ? `-active -${dir}` : ''"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M0.810974 8.34668L5.5607 13.0964L10.3104 8.34668H0.810974Z"
                                fill="currentColor"
                            />
                            <path
                                d="M10.3105 5.09668L5.56076 0.346956L0.811037 5.09668L10.3105 5.09668Z"
                                fill="currentColor"
                            />
                        </svg>
                    </th>
                    <th class="sortHeader">Packing Slip</th>
                    <th class="sortHeader">
                        Tracking # <span class="asterisk">*</span>
                    </th>
                    <th class="sortHeader">Invoice</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="line_item in paginatedLineItems"
                    :key="line_item.lineNumber"
                >
                    <td>{{ line_item.lineNumber }}</td>
                    <td>{{ line_item.itemNumber }}</td>
                    <td>
                        {{ line_item.description }}
                    </td>
                    <td>
                        <span>Ordered:</span> {{ line_item.orderedQuantity
                        }}<br />
                        <span>Shipped:</span> {{ line_item.deliveredQuantity }}
                    </td>
                    <td>
                        {{ formatCurrency(line_item.netPrice) }}
                        <span>USD</span>
                    </td>

                    <td>
                        <div
                            class="status"
                            :class="`-${line_item.status.toLowerCase()}`"
                        >
                            <span></span>{{ line_item.status }}
                        </div>
                    </td>

                    <td v-html="linkPackingSlipNumbers(line_item.deliveryNumbers)"></td>

                    <td>
                        <tracking-number
                            v-for="n in line_item.trackingNumbers"
                            :key="n"
                            :number="n"
                            target="_blank"
                        />
                    </td>
                    <td
                        v-html="linkInvoiceNumbers(line_item.invoiceNumbers)"
                    ></td>
                </tr>
            </tbody>
        </table>
        <div v-if="pages > 1">
            <div>
                Showing {{ resultsDisplayed }} of
                {{ filteredLineItems.length }} Results
            </div>
            <nav class="mop-pagination">
                <ul class="mop-pagination__list">
                    <li v-if="page > 1">
                        <a
                            class="mop-pagination__item mop-pagination__item--first"
                            @click="setPage(1)"
                            >first</a
                        >
                    </li>
                    <li v-if="page > 1">
                        <a
                            class="mop-pagination__item mop-pagination__item--previous"
                            @click="setPage(page - 1)"
                            >previous</a
                        >
                    </li>
                    <li v-for="n in pages">
                        <a
                            class="mop-pagination__item"
                            :class="n == page ? '--active' : ''"
                            @click="setPage(n)"
                        >
                            {{ n }}
                        </a>
                    </li>
                    <li v-if="page < pages">
                        <a
                            class="mop-pagination__item mop-pagination__item--next"
                            @click="setPage(page + 1)"
                            >next</a
                        >
                    </li>
                    <li v-if="page < pages">
                        <a
                            class="mop-pagination__item mop-pagination__item--last"
                            @click="setPage(pages)"
                            >last</a
                        >
                    </li>
                </ul>
            </nav>
        </div>
        <div class="note">
            <div class="asterisk">*</div>
            Note - only shipments sent via FedEx, UPS or USPS will have links to
            tracking status. Other carriers require verification via external
            tracking sites.
        </div>
    </div>
</template>
